import { SortQuery, Filters } from "@/services/api/types";
import { ViewType } from "./types";
import { SortingState } from "@tanstack/react-table";
import { normalizeDateRange } from "@/utils/dateRange";
import { WITHDRAWN_FILTER_KEYS } from "@/entities/dataTables/withdrawnFilterKeys";
import { has, isPlainObject } from "lodash";

export const isValidConfigResult = (data: unknown): boolean => {
  return data !== null && (typeof data === "object" || data === "");
};

export const isViewTypeTable = (type: ViewType): boolean => {
  return type !== ViewType.DEALS_KANBAN;
};

export const sortQueryToState = (
  query?: SortQuery,
  defaultField: string = "createdAt"
): SortingState => {
  if (!query) return [{ id: defaultField, desc: false }];
  return [{ id: query.field, desc: !query.ascending }];
};

export const sortStateToQuery = (
  state: SortingState,
  defaultField: string = "createdAt"
): SortQuery => {
  const defaultSort = { field: defaultField, ascending: true };
  if (!state.length) return defaultSort;
  return {
    field: state[0].id,
    ascending: !state[0].desc
  };
};

export const CURRENT_USER_PLACEHOLDER = "$me";

/**
 * Recursively replaces every occurrence of CURRENT_USER_PLACEHOLDER in a
 * filters object with the supplied userId. Handles arrays, plain objects, and
 * scalar values. Returns the original object unchanged when userId is absent.
 */
export const resolveCurrentUserFilters = (
  filters: Filters,
  userId: string | undefined
): Filters => {
  if (!userId || !filters || typeof filters !== "object") return filters;

  const resolve = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map((item) =>
        item === CURRENT_USER_PLACEHOLDER ? userId : resolve(item)
      );
    }
    // Plain objects only — rebuilding a Date this way yields {}, not a date.
    if (isPlainObject(value)) {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [
          k,
          resolve(v)
        ])
      );
    }
    return value === CURRENT_USER_PLACEHOLDER ? userId : value;
  };

  return resolve(filters) as Filters;
};

// Rehydrates one date filter value's custom range; null means drop the filter.
const normalizeDateFilterValue = (
  dateFilterValue: Record<string, unknown>
): Record<string, unknown> | null => {
  const dateRange = dateFilterValue.dateRange as
    | Record<string, unknown>
    | string
    | undefined;

  if (!dateRange || typeof dateRange !== "object") return dateFilterValue;

  // A lone toDate is still a usable upper bound, so it survives even though
  // normalizeDateRange insists on a fromDate; neither bound means no filter.
  if (!dateRange.fromDate) {
    return dateRange.toDate ? dateFilterValue : null;
  }

  const normalizedRange = normalizeDateRange(dateRange);
  if (!normalizedRange) return null;

  return { ...dateFilterValue, dateRange: normalizedRange };
};

// converting string dates back to Date objects
export const normalizeFiltersAfterDeserialization = (
  filters: Filters
): Filters => {
  if (!filters || typeof filters !== "object") return filters;

  const normalizedFilters = { ...filters };

  Object.keys(normalizedFilters).forEach((key) => {
    // A view saved before these stopped being offered still carries the filter,
    // and the picker no longer renders a chip for it — so it would keep
    // narrowing the result set to nothing with nothing to remove. Dropped by
    // exact key rather than by "absent from the current options", because a
    // filter on a hidden column is legitimately applied while absent from them.
    if (WITHDRAWN_FILTER_KEYS.has(key)) {
      delete normalizedFilters[key];
      return;
    }

    const filterValue = normalizedFilters[key];
    if (!filterValue || typeof filterValue !== "object") return;

    // "Does not contain" used to save the case-sensitive operator; a view saved
    // then must filter the same way "Contains" does now.
    if (isPlainObject(filterValue) && has(filterValue, "not_contains")) {
      const { not_contains, ...rest } = filterValue as Record<string, unknown>;
      normalizedFilters[key] = { ...rest, not_contains_nocase: not_contains };
      return;
    }

    // An "is not" condition nests the range one level down, so normalize the
    // inner value and rewrap. Mirrors isNotInFilter in hooks/useFilterOptions,
    // reimplemented here because importing it would cycle back through
    // entities/dataTables/helpers.
    const isNegated = isPlainObject(filterValue) && has(filterValue, "not_in");
    const dateFilterValue = isNegated
      ? (filterValue as { not_in: unknown }).not_in
      : filterValue;

    if (
      !dateFilterValue ||
      typeof dateFilterValue !== "object" ||
      !("dateRange" in dateFilterValue)
    ) {
      return;
    }

    const normalizedValue = normalizeDateFilterValue(
      dateFilterValue as Record<string, unknown>
    );

    // Unparseable bounds can never filter usefully, so drop the key.
    if (!normalizedValue) {
      delete normalizedFilters[key];
      return;
    }

    normalizedFilters[key] = isNegated
      ? { ...filterValue, not_in: normalizedValue }
      : normalizedValue;
  });

  return normalizedFilters;
};
